/* eslint-disable jsx-a11y/label-has-associated-control */
import { LanguageCode } from '@/ifaces/language';
import { UserType } from '@/ifaces/user';
import { UserResource } from '@clerk/types';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Loader2 } from 'lucide-react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useLanguages } from '../hooks/use-languages';
import {
  triggerHideLanguageSwitcherEvent,
  triggerShowLanguageSwitcherEvent,
} from '../util/custom-events';
import { loadCatalog } from '../util/i18n';
import { BubbleTrail } from './BubbleTrail';
import { jwplayer } from './JWPlayer';
import { ModalLayout } from './TutorPanels';
import { Text } from './Typography';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

/**
 * This is the onboarding wizard for the Tutor app.
 *
 * There are several parts to onboarding:
 *   1. Intro Video (3 screens atm)
 *   2. Collect Name
 *   3. Collect Section Company Info (Consumer Only)
 *   4. Collect Company/Role
 *   5. Offer the assessment or take them right into training
 */

const jobLevels = [
  { value: 'associate_staff', text: msg`Associate/Staff` },
  { value: 'manager', text: msg`Manager` },
  { value: 'director', text: msg`Director` },
  { value: 'department_head', text: msg`Department head` },
  { value: 'evp_vp', text: msg`EVP, VP` },
  { value: 'c_suite', text: msg`C-Suite (ie. COO, CIO, CTO, CHRO)` },
  { value: 'consultant', text: msg`Consultant` },
  { value: 'self_employed', text: msg`Self-employed` },
  { value: 'other', text: msg`Other` },
];
const companySizes = [
  { value: '1-99', text: msg`1-99 employees` },
  { value: '100-499', text: msg`100-499 employees` },
  { value: '500-999', text: msg`500-999 employees` },
  { value: '1000-2499', text: msg`1,000-2,499 employees` },
  { value: '2500-4999', text: msg`2,500-4,999 employees` },
  { value: '5000-9999', text: msg`5,000-9,999 employees` },
  { value: '10000+', text: msg`10,000+ employees` },
];
const jobFunctions = [
  { value: 'Marketing/Advertising', text: msg`Marketing/Advertising` },
  { value: 'Engineering/It', text: msg`Engineering/IT` },
  { value: 'Accounting/Finance', text: msg`Accounting/Finance` },
  { value: 'Operations/Supply Chain', text: msg`Operations/Supply Chain` },
  { value: 'Product Management', text: msg`Product Management` },
  { value: 'Human Resources/People', text: msg`Human Resources/People` },
  { value: 'Data/Analytics', text: msg`Data/Analytics` },
  { value: 'Learning & Development', text: msg`Learning & Development` },
  { value: 'Legal/Compliance', text: msg`Legal/Compliance` },
  { value: 'Sales/Revenue', text: msg`Sales/Revenue` },
  { value: 'Customer Support/Success', text: msg`Customer Support/Success` },
  { value: 'Other', text: msg`Other` },
];
const industries = [
  { value: 'Banking', text: msg`Banking` },
  { value: 'Consulting', text: msg`Consulting` },
  { value: 'Finance', text: msg`Finance` },
  { value: 'Healthcare', text: msg`Healthcare` },
  { value: 'Manufacturing', text: msg`Manufacturing` },
  { value: 'Retail', text: msg`Retail` },
  { value: 'Technology', text: msg`Technology` },
  { value: 'Transportation', text: msg`Transportation` },
  { value: 'Other', text: msg`Other` },
];
enum Step {
  LANGUAGE = 0,
  INTRO = 1,
  COLLECT_NAME = 2,
  COLLECT_SECTION = 3,
  COLLECT_COMPANY = 4,
}
const MAX = Step.COLLECT_COMPANY;
type Data = {
  first_name: string;
  last_name: string;
  company: string;
  role: string;
  language_code: string;
  company_name: string;
  title: string;
  job_level: string;
  number_of_employees: string;
  job_function: string;
  industry: string;
};

type StepProps = {
  step: Step;
  setStep: (step: Step) => void;
  data: Data;
  setData: (data: Data) => void;
  onFinish: (data: Data) => void;
};

export function OnboardingWizard({
  user,
  userType,
  onFinish,
}: {
  user: UserResource;
  userType: UserType | null;
  onFinish(data: Data): void;
}) {
  const [step, setStep] = useState(Step.LANGUAGE);
  const [data, setData] = useState<Data>({
    first_name: user.firstName || '',
    last_name: user.lastName || '',
    company: '',
    role: '',
    language_code: LanguageCode.English,
    company_name: '',
    title: '',
    job_level: jobLevels[0].value,
    number_of_employees: companySizes[0].value,
    job_function: jobFunctions[0].value,
    industry: industries[0].value,
  });
  useEffect(() => {
    triggerHideLanguageSwitcherEvent();
    return () => {
      triggerShowLanguageSwitcherEvent();
    };
  }, []);
  const stepProps = { step, setStep, data, setData, onFinish, userType };
  return step === Step.LANGUAGE ? (
    <OnboardingLanguage {...stepProps} />
  ) : step === Step.INTRO ? (
    <OnboardingIntro {...stepProps} />
  ) : step === Step.COLLECT_NAME ? (
    <OnboardingCollectName {...stepProps} />
  ) : step === Step.COLLECT_SECTION ? (
    <OnboardingCollectSection {...stepProps} />
  ) : step === Step.COLLECT_COMPANY ? (
    <OnboardingCollectCompany {...stepProps} />
  ) : null;
}

function OnboardingLanguage(props: {
  step: Step;
  setStep: (step: Step) => void;
  data: Data;
  setData: (data: Data) => void;
}) {
  const { languages } = useLanguages();
  return (
    <ModalLayout>
      <div className="flex justify-center flex-col">
        <Text spaced header semiBold m>
          <Trans>Choose your language</Trans>
        </Text>
        <Text>
          <Trans>This will help us personalize your experience.</Trans>
        </Text>
      </div>
      <Select
        value={props.data.language_code}
        onValueChange={(value) => props.setData({ ...props.data, language_code: value })}
      >
        <SelectTrigger className="w-full mt-5 mb-35 focus-visible:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {languages?.map((language) => (
              <SelectItem key={language.code} value={language.code}>
                {language.flag} {language.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <BottomBar>
        <BackButton onBack={() => {}} disabled />
        <BubbleTrail step={Step.LANGUAGE} steps={MAX} />
        <ForwardButton
          onClick={async () => {
            await loadCatalog(props.data.language_code);
            props.setStep(Step.INTRO);
          }}
        />
      </BottomBar>
    </ModalLayout>
  );
}

function OnboardingIntro(props: StepProps) {
  const ref = useRef(null);
  const languageCode = props.data.language_code;
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  useEffect(() => {
    const player = jwplayer(ref.current).setup({
      playlist: 'https://cdn.jwplayer.com/v2/media/gNvktLUO',
      width: '100%',
      aspectratio: '16:9',
    });
    player.on('ready', () => setIsVideoLoading(false));
    player.on('firstFrame', () => {
      const audioTracks = player.getAudioTracks();
      if (audioTracks?.length > 0) {
        const trackIndex = audioTracks.find(
          (track: { language: string; hlsjsIndex: number }) => track.language === languageCode,
        );
        if (trackIndex) player.setCurrentAudioTrack(trackIndex.hlsjsIndex);
      }
      player.seek(0);
    });
    return () => player.remove();
  }, [languageCode]);

  return (
    <ModalLayout hideHeader>
      <Text l semiBold header>
        <Trans>Welcome to ProfAI</Trans>
      </Text>
      <Text l spaced>
        <Trans>Your personal AI coach.</Trans>
      </Text>
      <div className="flex justify-center items-center h-64">
        <div ref={ref} />
        {isVideoLoading && <Loader2 className="h-12 w-12 animate-spin text-primary" />}
      </div>
      <BottomBar>
        <BackButton onBack={() => props.setStep(Step.LANGUAGE)} />
        <BubbleTrail step={Step.INTRO} steps={MAX} />
        <ForwardButton onClick={() => props.setStep(Step.COLLECT_NAME)} />
      </BottomBar>
    </ModalLayout>
  );
}

function OnboardingCollectName(props: {
  step: Step;
  setStep: (step: Step) => void;
  data: Data;
  setData: (data: Data) => void;
  onFinish: (data: Data) => void;
  userType: UserType | null;
}) {
  const hasBothNames = (props.data.first_name ?? '').trim() && (props.data.last_name ?? '').trim();
  const showSectionForm = props.userType === UserType.Consumer;
  return (
    <ModalLayout>
      <div className="flex justify-center flex-col items-center gap-3">
        <Text>
          <Trans>Let&apos;s start with the basics.</Trans>
        </Text>
        <Text spaced header semiBold m>
          <Trans>What should I call you?</Trans>
        </Text>
      </div>
      <label>
        <Text warm75 style={{ marginBottom: 10 }}>
          <Trans>First Name</Trans>
        </Text>
        <input
          type="text"
          className="admin block w-full"
          value={props.data.first_name}
          onChange={(e) => props.setData({ ...props.data, first_name: e.target.value })}
        />
      </label>
      <label>
        <Text warm75 style={{ marginBottom: 10, marginTop: 30 }}>
          <Trans>Last Name</Trans>
        </Text>
        <input
          type="text"
          className="admin block w-full"
          value={props.data.last_name}
          onChange={(e) => props.setData({ ...props.data, last_name: e.target.value })}
        />
      </label>
      <div className="flex justify-center mt-8 mb-4">
        <Text warm75>
          <Trans>You can change this at any time.</Trans>
        </Text>
      </div>
      <BottomBar>
        <BackButton onBack={() => props.setStep(Step.INTRO)} />
        <BubbleTrail step={Step.COLLECT_NAME} steps={MAX} />
        <ForwardButton
          title={!hasBothNames ? t`Please enter your first and last name` : ''}
          disabled={!hasBothNames}
          onClick={() => {
            if (!hasBothNames) return;
            props.setStep(showSectionForm ? Step.COLLECT_SECTION : Step.COLLECT_COMPANY);
          }}
        />
      </BottomBar>
    </ModalLayout>
  );
}

function OnboardingCollectSection(props: {
  step: Step;
  setStep: (step: Step) => void;
  data: Data;
  setData: (data: Data) => void;
  onFinish: (data: Data) => void;
}) {
  const { _ } = useLingui();
  const hasSectionDetails =
    (props.data.company_name ?? '').trim() && (props.data.title ?? '').trim();
  return (
    <ModalLayout>
      <div className="flex justify-center flex-col items-center gap-3">
        <Text header semiBold m>
          <Trans>Tell us about yourself</Trans>
        </Text>
        <Text spaced l center>
          <Trans>Give us a few details to get started</Trans>
        </Text>
      </div>
      <label>
        <Text warm75 style={{ marginBottom: 10 }}>
          <Trans>Company Name</Trans>
        </Text>
        <input
          type="text"
          className="admin block w-full"
          value={props.data.company_name}
          placeholder={t`Company Name`}
          onChange={(e) => props.setData({ ...props.data, company_name: e.target.value })}
        />
      </label>
      <label>
        <Text warm75 style={{ marginBottom: 10, marginTop: 30 }}>
          <Trans>Job Title</Trans>
        </Text>
        <input
          type="text"
          className="admin block w-full"
          value={props.data.title}
          placeholder={t`Job Title`}
          onChange={(e) => props.setData({ ...props.data, title: e.target.value })}
        />
      </label>
      <label>
        <Text warm75 style={{ marginBottom: 10, marginTop: 30 }}>
          <Trans>Job Level</Trans>
        </Text>
        <Select
          value={props.data.job_level}
          onValueChange={(value) => props.setData({ ...props.data, job_level: value })}
        >
          <SelectTrigger className="w-full focus-visible:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {jobLevels?.map((jobLevel) => (
                <SelectItem key={jobLevel.value} value={jobLevel.value}>
                  {_(jobLevel.text)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </label>
      <label>
        <Text warm75 style={{ marginBottom: 10, marginTop: 30 }}>
          <Trans>Company Size</Trans>
        </Text>
        <Select
          value={props.data.number_of_employees}
          onValueChange={(value) => props.setData({ ...props.data, number_of_employees: value })}
        >
          <SelectTrigger className="w-full focus-visible:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {companySizes?.map((companySize) => (
                <SelectItem key={companySize.value} value={companySize.value}>
                  {_(companySize.text)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </label>
      <label>
        <Text warm75 style={{ marginBottom: 10, marginTop: 30 }}>
          <Trans>Job Function</Trans>
        </Text>
        <Select
          value={props.data.job_function}
          onValueChange={(value) => props.setData({ ...props.data, job_function: value })}
        >
          <SelectTrigger className="w-full focus-visible:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {jobFunctions?.map((jobFunction) => (
                <SelectItem key={jobFunction.value} value={jobFunction.value}>
                  {_(jobFunction.text)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </label>
      <label>
        <Text warm75 style={{ marginBottom: 10, marginTop: 30 }}>
          <Trans>Industry</Trans>
        </Text>
        <Select
          value={props.data.industry}
          onValueChange={(value) => props.setData({ ...props.data, industry: value })}
        >
          <SelectTrigger className="w-full mb-10 focus-visible:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {industries?.map((industry) => (
                <SelectItem key={industry.value} value={industry.value}>
                  {_(industry.text)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </label>
      <BottomBar>
        <BackButton onBack={() => props.setStep(Step.COLLECT_NAME)} />
        <BubbleTrail step={Step.COLLECT_SECTION} steps={MAX} />
        <ForwardButton
          title={!hasSectionDetails ? t`Please enter your details.` : ''}
          disabled={!hasSectionDetails}
          onClick={() => {
            if (!hasSectionDetails) return;
            props.setStep(Step.COLLECT_COMPANY);
          }}
        />
      </BottomBar>
    </ModalLayout>
  );
}

function OnboardingCollectCompany(props: {
  step: Step;
  setStep: (step: Step) => void;
  data: Data;
  setData: (data: Data) => void;
  onFinish: (data: Data) => void;
  userType: UserType | null;
}) {
  const hasCompanyDetails = (props.data.company ?? '').trim() && (props.data.role ?? '').trim();
  const showSectionForm = props.userType === UserType.Consumer;
  return (
    <ModalLayout>
      <div className="flex justify-center flex-col items-center gap-3">
        <Text header semiBold m>
          <Trans>Tell us a bit about your work.</Trans>
        </Text>
        <Text spaced l center>
          <Trans>
            The more we know about your role, the better we can personalize your learning path.
          </Trans>
        </Text>
      </div>
      <label>
        <Text warm75 style={{ marginBottom: 10 }}>
          <Trans>Industry or company</Trans>
        </Text>
        <textarea
          className="admin block w-full"
          placeholder={t`e.g. Educational startup using AI for personalized tutoring`}
          value={props.data.company}
          onChange={(e) => props.setData({ ...props.data, company: e.target.value })}
        />
      </label>
      <label>
        <Text warm75 style={{ marginBottom: 10, marginTop: 30 }}>
          <Trans>Your role or responsibilities</Trans>
        </Text>
        <textarea
          className="admin block w-full"
          placeholder={t`e.g. Senior UX Designer creating AI-driven course flows`}
          value={props.data.role}
          onChange={(e) => props.setData({ ...props.data, role: e.target.value })}
        />
      </label>
      <Text center m spaced style={{ padding: '0 40px', margin: 10 }}>
        <Trans>
          We use this information to personalize your content, you can update it at any time.
        </Trans>
      </Text>
      <BottomBar>
        <BackButton
          onBack={() => props.setStep(showSectionForm ? Step.COLLECT_SECTION : Step.COLLECT_NAME)}
        />
        <BubbleTrail step={Step.COLLECT_COMPANY} steps={MAX} />
        <ForwardButton
          title={!hasCompanyDetails ? t`Please enter your details.` : ''}
          disabled={!hasCompanyDetails}
          onClick={() => {
            if (!hasCompanyDetails) return;
            props.onFinish({ ...props.data });
          }}
        />
      </BottomBar>
    </ModalLayout>
  );
}

function BottomBar({ children }: PropsWithChildren) {
  return <div className="flex items-center justify-between mt-8">{children}</div>;
}

function BackButton({ disabled, onBack }: { disabled?: boolean; onBack: () => void }) {
  return (
    <button
      className="ghost"
      disabled={disabled}
      onClick={() => !disabled && onBack()}
      style={{
        color: disabled ? 'var(--colors-warm-30)' : 'var(--colors-warm-90)',
        marginRight: 40, // try to make it take up the same amount of space as the forward button
      }}
    >
      <svg
        width="18"
        height="13"
        viewBox="0 0 18 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.61366 0.21967C6.90656 0.512563 6.90656 0.987437 6.61366 1.28033L1.81066 6.08333L6.61366 10.8863C6.90656 11.1792 6.90656 11.6541 6.61366 11.947C6.32077 12.2399 5.8459 12.2399 5.553 11.947L0.21967 6.61366C-0.0732233 6.32077 -0.0732233 5.8459 0.21967 5.553L5.553 0.21967C5.8459 -0.0732233 6.32077 -0.0732233 6.61366 0.21967Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.355469 6.08341C0.355469 5.6692 0.691255 5.33341 1.10547 5.33341H16.7499C17.1641 5.33341 17.4999 5.6692 17.4999 6.08341C17.4999 6.49762 17.1641 6.83341 16.7499 6.83341H1.10547C0.691255 6.83341 0.355469 6.49762 0.355469 6.08341Z"
          fill="currentColor"
        />
      </svg>
    </button>
  );
}

export function ForwardButton(props: { title?: string; disabled?: boolean; onClick(): void }) {
  return (
    <button
      disabled={props.disabled}
      title={props.title}
      className="neo flex items-center gap-2"
      onClick={() => props.onClick()}
    >
      <Text m>
        <Trans>Next</Trans>
      </Text>
      <svg
        width="18"
        height="12"
        viewBox="0 0 23 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M2.22475 10.3166H18.4136L12.9147 15.7544C12.4292 16.2497 12.4394 17.0414 12.93 17.5303C13.4245 18.0154 14.225 18.0256 14.7258 17.5455L22.4112 9.94549C22.6521 9.70862 22.7865 9.38689 22.7865 9.05503V9.04996C22.7865 8.88656 22.752 8.71809 22.6866 8.56483C22.6213 8.41156 22.5265 8.27349 22.4112 8.15442L14.7258 0.554425C14.2199 0.0743579 13.4245 0.0844912 12.93 0.569625C12.4343 1.05856 12.4292 1.85022 12.9147 2.34549L18.4136 7.78329H2.22475C1.51897 7.78329 0.943848 8.35203 0.943848 9.04996C0.943848 9.74789 1.51897 10.3166 2.22475 10.3166Z"
          fill="currentColor"
        />
      </svg>
    </button>
  );
}
